import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import translate from '../../../../commonComponents/translate';
import CheckIcon from '../../../../commonComponents/icons/CheckIcon';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';

const LevelCard = props => {
  const { t, index, levelData } = props;

  const {
    level,
    skillLevel,
    personalVA,
    levelGroupVA,
    levelLevelMinimumCount,
    daysLeft,
  } = levelData;

  const isLevelComplete = cardIndex => {
    if (cardIndex === 0) {
      return level > 0;
    }
    return false;
  };

  const isStageComplete = stageObject => stageObject?.limitAchieved;

  const numberOfDaysInCurrentMonth = moment().daysInMonth();
  const levelProgress = () =>
    ((numberOfDaysInCurrentMonth - daysLeft || 0) /
      numberOfDaysInCurrentMonth) *
    100;

  const stageProgress = stageObject =>
    ((stageObject.progress * 100) / stageObject.limit)?.toFixed(0) || 0;

  const { loggedUser } = useContext(LoggedUserContext);
  const { organization } = useContext(ConfigsContext);
  const getLevelName = levelNumber =>
    organization?.skillLevels[levelNumber]?.nameTranslations[
      loggedUser.language
    ];

  return (
    <div className={`level-card card-${index + 1}`}>
      {isLevelComplete(index) && (
        <div className="cardBookmark">
          <span className="cardBookmarkText">
            {t('report.arcova.levelCompleted')}
            <img
              src="/images/reports/party-hat.svg"
              alt="yay"
              className="partyHat"
            />
          </span>
        </div>
      )}
      <div className="cardHeader">
        <span className="levelLabel">
          {t('report.arcova.level')} {level}
        </span>
        <img
          src={skillLevel?.icon}
          alt={skillLevel?.name}
          className="level-card-image"
        />
        {isLevelComplete(index) && (
          <div className="completedLevelCheck">
            <CheckIcon />
          </div>
        )}
        <span className="levelLabelName">{skillLevel?.name}</span>
        {index === 1 && (
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${levelProgress()}%`,
              }}
            >
              {daysLeft || '-'}{' '}
              {daysLeft === 1 ? t('dayLeft') : t('report.arcova.daysLeft')}
            </div>
          </div>
        )}
      </div>
      <div className="cardTableBody">
        <table className="table" style={{ width: '100%' }}>
          <tbody>
            <tr className="content" key="row-1">
              <td className="contentCell">
                {isStageComplete(personalVA) ? (
                  <div className="completedStageCheck">
                    <CheckIcon color="#3FBC5F" />
                  </div>
                ) : (
                  <div className="progress-half-circle">
                    <div className="barOverflow">
                      <div
                        className="bar"
                        style={{
                          transform: `rotate(${
                            45 + stageProgress(personalVA) * 1.8
                          }deg)`,
                        }}
                      ></div>
                    </div>
                    <span>{stageProgress(personalVA)}</span>%
                  </div>
                )}
                <span className="contentCellText">
                  &gt;=&nbsp;{personalVA?.limit}
                </span>
              </td>
            </tr>
            <tr className="content" key="row-2">
              <td className="contentCell">
                {isStageComplete(levelGroupVA) ? (
                  <div className="completedStageCheck">
                    <CheckIcon color="#3FBC5F" />
                  </div>
                ) : (
                  <div className="progress-half-circle">
                    <div className="barOverflow">
                      <div
                        className="bar"
                        style={{
                          transform: `rotate(${
                            45 + stageProgress(levelGroupVA) * 1.8
                          }deg)`,
                        }}
                      ></div>
                    </div>
                    <span>{stageProgress(levelGroupVA)}</span>%
                  </div>
                )}
                <span className="contentCellText">
                  &gt;=&nbsp;{levelGroupVA?.limit}
                </span>
              </td>
            </tr>
            <tr className="content" key="row-3">
              <td className="contentCell">
                <div className="completedStageCheck">
                  {isStageComplete(levelLevelMinimumCount) && (
                    <CheckIcon color="#3FBC5F" />
                  )}
                </div>
                <span className="contentCellText">
                  {levelLevelMinimumCount?.limit?.map(l => (
                    <span>
                      &gt;=&nbsp;{l?.minimumAmount}&nbsp;
                      {t('report.laborigin.objectives.distributorLevel')}&nbsp;
                      {getLevelName(l?.minimumLevel)}
                    </span>
                  ))}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

LevelCard.propTypes = {
  t: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  levelData: PropTypes.shape({
    level: PropTypes.number.isRequired,
    daysLeft: PropTypes.number.isRequired,
    skillLevel: PropTypes.shape({
      icon: PropTypes.string,
      name: PropTypes.string,
    }).isRequired,
    levelGroupVA: PropTypes.shape({
      limit: PropTypes.number,
      limitAchieved: PropTypes.bool,
      progress: PropTypes.number,
    }).isRequired,
    levelLevelMinimumCount: PropTypes.shape({
      limit: PropTypes.arrayOf(
        PropTypes.shape({
          minimumAmount: PropTypes.number,
          minimumLevel: PropTypes.number,
        }),
      ),
      limitAchieved: PropTypes.bool,
    }).isRequired,
    personalVA: PropTypes.shape({
      limit: PropTypes.number,
      limitAchieved: PropTypes.bool,
      progress: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

export default translate(LevelCard);
